import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { PrintPageComponent } from '@next/core-lib/components';
import { AgrDatePipe, TranslatePipe } from '@next/core-lib/i18n';

import { DairyYieldScanSummary } from '../../models/dairy-yield-scan.models';
import { DairyYieldScanActionPlanDraftStore } from '../../services/dairy-yield-scan-action-plan-draft.store';
import { DairyYieldScanCategoryPotentialsComponent } from '../dairy-yield-scan-category-potentials';
import { DairyYieldScanCustomerDetailsComponent } from '../dairy-yield-scan-customer-details/dairy-yield-scan-customer-details.component';
import { DairyYieldScanInsightsComponent } from '../dairy-yield-scan-insights/dairy-yield-scan-insights.component';

const DAYS_PER_YEAR = 365;
const KILOGRAMS_PER_HUNDRED = 100;
@Component({
  selector: 'app-dairy-yield-scan-print-summary',
  templateUrl: './dairy-yield-scan-print-summary.component.html',
  styleUrls: ['./dairy-yield-scan-print-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgrDatePipe,
    DairyYieldScanCategoryPotentialsComponent,
    DairyYieldScanCustomerDetailsComponent,
    DairyYieldScanInsightsComponent,
    DecimalPipe,
    MatIconModule,
    PrintPageComponent,
    TranslatePipe,
  ],
})
export class DairyYieldScanPrintSummaryComponent {
  @Input({ required: true }) summary!: DairyYieldScanSummary;
  @Input({ required: true }) milkPrice: number | null = null;
  @Input() currentMilkYield: number | null = null;
  @Input() targetMilkYield: number | null = null;

  readonly actionPlan = inject(DairyYieldScanActionPlanDraftStore).actionPlan;

  priorityLabel(priority: string): string {
    return this.summary.actionPoints.some((improvement) => improvement.questionKey === priority)
      ? `${priority}.actionpoint`
      : priority;
  }

  annualPotentialMilk(): number | null {
    return this.summary.dairyCowCount === null
      ? null
      : this.summary.baselinePotential * this.summary.dairyCowCount * DAYS_PER_YEAR;
  }

  annualTargetMilk(): number | null {
    if (
      this.summary.dairyCowCount === null ||
      this.summary.currentMilkProduction === null ||
      this.summary.targetMilkProduction === null
    ) {
      return null;
    }

    return (
      Math.max(0, this.summary.targetMilkProduction - this.summary.currentMilkProduction) *
      this.summary.dairyCowCount *
      DAYS_PER_YEAR
    );
  }

  annualPotentialRevenue(): number | null {
    return this.calculateRevenue(this.annualPotentialMilk());
  }

  annualTargetRevenue(): number | null {
    return this.calculateRevenue(this.annualTargetMilk());
  }

  hasMilkYieldData(): boolean {
    return (
      typeof this.currentMilkYield === 'number' &&
      Number.isFinite(this.currentMilkYield) &&
      typeof this.targetMilkYield === 'number' &&
      Number.isFinite(this.targetMilkYield)
    );
  }

  selectedActionPotential(): number {
    return (this.actionPlan()?.priorities ?? []).reduce(
      (total, questionKey) =>
        total +
        (this.summary.actionPoints.find((action) => action.questionKey === questionKey)
          ?.potential ?? 0),
      0,
    );
  }

  improvedMilkProduction(): number | null {
    return this.currentMilkYield === null
      ? null
      : this.currentMilkYield + this.selectedActionPotential();
  }

  remainingToTarget(): number | null {
    const improvedProduction = this.improvedMilkProduction();
    return improvedProduction === null || this.targetMilkYield === null
      ? null
      : Math.max(0, this.targetMilkYield - improvedProduction);
  }

  private calculateRevenue(annualMilk: number | null): number | null {
    return annualMilk === null || this.milkPrice === null || this.milkPrice < 0
      ? null
      : (annualMilk * this.milkPrice) / KILOGRAMS_PER_HUNDRED;
  }
}
